import React, { useEffect, useState } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { isAdminOrSuperAdmin } from '../utils/auth';
import api from '../services/api';
import LoadingSpinner from '../components/LoadingSpinner';
import '../App.css';

interface TournamentTeam {
  id: string;
  name: string;
  teamNumber?: string;
  seed: number | null;
  groupName: string | null;
}

interface TournamentMatch {
  id: string;
  roundId: string;
  bracketPosition: number;
  bracketType: string;
  homeTeam: { id: string; name: string } | null;
  awayTeam: { id: string; name: string } | null;
  homeScore: number | null;
  awayScore: number | null;
  status: string;
  scheduledDate: string;
  location: string;
}

interface Round {
  id: string;
  roundNumber: number;
  roundName: string;
  roundType: string;
}

interface Tournament {
  id: string;
  name: string;
  format: string;
  status: string;
  startDate?: string;
  endDate?: string;
  teams: TournamentTeam[];
  rounds: Round[];
  matches: TournamentMatch[];
}

const formatLabels: Record<string, string> = {
  single_elimination: 'Single Elimination',
  double_elimination: 'Double Elimination',
  round_robin: 'Round Robin',
  group_knockout: 'Group + Knockout',
};

const TournamentManagement: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [tournament, setTournament] = useState<Tournament | null>(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState<'overview' | 'teams' | 'draw' | 'matches'>('overview');
  const [actionLoading, setActionLoading] = useState(false);
  const [error, setError] = useState('');
  const [availableTeams, setAvailableTeams] = useState<any[]>([]);
  const [selectedAddTeamId, setSelectedAddTeamId] = useState('');

  const isAdmin = isAdminOrSuperAdmin(user?.profileType);

  const fetchTournament = async () => {
    try {
      const res = await api.get(`/tournaments/${id}`);
      setTournament(res.data);
    } catch (err) {
      console.error('Error fetching tournament:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchTournament();
  }, [id]);

  useEffect(() => {
    if (activeTab === 'teams') {
      api.get('/teams', { params: { limit: '100' } }).then(res => {
        setAvailableTeams(res.data.teams || []);
      });
    }
  }, [activeTab]);

  const handleGenerateDraw = async () => {
    if (!window.confirm('Generate the bracket? This will clear any existing matches.')) return;
    setActionLoading(true);
    setError('');
    try {
      await api.post(`/tournaments/${id}/draw`);
      await fetchTournament();
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to generate draw');
    } finally {
      setActionLoading(false);
    }
  };

  const handleAddTeam = async () => {
    if (!selectedAddTeamId) return;
    setActionLoading(true);
    setError('');
    try {
      await api.post(`/tournaments/${id}/teams`, {
        teamId: selectedAddTeamId,
        seed: (tournament?.teams?.length || 0) + 1,
      });
      setSelectedAddTeamId('');
      await fetchTournament();
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to add team');
    } finally {
      setActionLoading(false);
    }
  };

  const handleRemoveTeam = async (teamId: string) => {
    if (!window.confirm('Remove this team from the tournament?')) return;
    setActionLoading(true);
    try {
      await api.delete(`/tournaments/${id}/teams/${teamId}`);
      await fetchTournament();
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to remove team');
    } finally {
      setActionLoading(false);
    }
  };

  const handleAdvance = async (matchId: string) => {
    setActionLoading(true);
    setError('');
    try {
      await api.post(`/tournaments/${id}/advance`, { matchId });
      await fetchTournament();
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to advance winner');
    } finally {
      setActionLoading(false);
    }
  };

  const handleUpdateStatus = async (status: string) => {
    setActionLoading(true);
    try {
      await api.put(`/tournaments/${id}`, { status });
      await fetchTournament();
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to update status');
    } finally {
      setActionLoading(false);
    }
  };

  if (loading) return <LoadingSpinner fullPage />;
  if (!tournament) return <div className="page-container"><p>Tournament not found.</p></div>;

  const existingTeamIds = new Set(tournament.teams.map(t => t.id));
  const teamsNotInTournament = availableTeams.filter(t => !existingTeamIds.has(t.id));

  const roundMatches = (roundId: string) => tournament.matches.filter(m => m.roundId === roundId);

  return (
    <div className="page-container">
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
        <div>
          <h1 style={{ margin: 0 }}>{tournament.name}</h1>
          <p style={{ color: '#6c757d', margin: '4px 0' }}>
            {formatLabels[tournament.format] || tournament.format} &middot; {tournament.status}
          </p>
        </div>
        <Link to="/tournaments" className="btn btn-outline-secondary">Back to Tournaments</Link>
      </div>

      {error && <div className="alert alert-danger" style={{ marginBottom: '16px' }}>{error}</div>}

      {/* Status actions */}
      {isAdmin && (
        <div style={{ display: 'flex', gap: '8px', marginBottom: '16px', flexWrap: 'wrap' }}>
          {tournament.status === 'draft' && (
            <button className="btn btn-sm btn-outline-primary" onClick={() => handleUpdateStatus('registration')} disabled={actionLoading}>
              Open Registration
            </button>
          )}
          {tournament.status === 'completed' || tournament.status === 'cancelled' ? null : (
            <button className="btn btn-sm btn-outline-danger" onClick={() => handleUpdateStatus('cancelled')} disabled={actionLoading}>
              Cancel Tournament
            </button>
          )}
          <Link to={`/tournaments/${id}/bracket`} className="btn btn-sm btn-outline-success">View Bracket</Link>
        </div>
      )}

      {/* Tabs */}
      <div style={{ display: 'flex', gap: '0', borderBottom: '2px solid #dee2e6', marginBottom: '20px' }}>
        {(['overview', 'teams', 'draw', 'matches'] as const).map(tab => (
          <button
            key={tab}
            onClick={() => setActiveTab(tab)}
            style={{
              padding: '10px 20px', border: 'none', cursor: 'pointer',
              fontWeight: activeTab === tab ? 600 : 400,
              borderBottom: activeTab === tab ? '2px solid #0d6efd' : '2px solid transparent',
              marginBottom: '-2px', background: 'none',
              color: activeTab === tab ? '#0d6efd' : '#6c757d',
            }}
          >
            {tab.charAt(0).toUpperCase() + tab.slice(1)}
          </button>
        ))}
      </div>

      {/* Overview Tab */}
      {activeTab === 'overview' && (
        <div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px', maxWidth: '500px' }}>
            <div><strong>Format:</strong> {formatLabels[tournament.format]}</div>
            <div><strong>Status:</strong> {tournament.status}</div>
            <div><strong>Teams:</strong> {tournament.teams.length}</div>
            <div><strong>Rounds:</strong> {tournament.rounds.length}</div>
            <div><strong>Matches:</strong> {tournament.matches.length}</div>
            {tournament.startDate && <div><strong>Start:</strong> {new Date(tournament.startDate).toLocaleDateString()}</div>}
            {tournament.endDate && <div><strong>End:</strong> {new Date(tournament.endDate).toLocaleDateString()}</div>}
          </div>
        </div>
      )}

      {/* Teams Tab */}
      {activeTab === 'teams' && (
        <div>
          {isAdmin && tournament.status !== 'completed' && (
            <div style={{ display: 'flex', gap: '8px', marginBottom: '16px' }}>
              <select
                value={selectedAddTeamId}
                onChange={e => setSelectedAddTeamId(e.target.value)}
                style={{ flex: 1, padding: '8px 12px', borderRadius: '6px', border: '1px solid #dee2e6' }}
              >
                <option value="">Select a team to add...</option>
                {teamsNotInTournament.map((t: any) => (
                  <option key={t.id} value={t.id}>{t.name} (#{t.teamNumber})</option>
                ))}
              </select>
              <button className="btn btn-primary" onClick={handleAddTeam} disabled={!selectedAddTeamId || actionLoading}>
                Add Team
              </button>
            </div>
          )}

          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr style={{ borderBottom: '2px solid #dee2e6' }}>
                <th style={{ padding: '8px', textAlign: 'left' }}>Seed</th>
                <th style={{ padding: '8px', textAlign: 'left' }}>Team</th>
                {tournament.format === 'group_knockout' && <th style={{ padding: '8px', textAlign: 'left' }}>Group</th>}
                {isAdmin && <th style={{ padding: '8px', textAlign: 'right' }}>Actions</th>}
              </tr>
            </thead>
            <tbody>
              {tournament.teams.map(team => (
                <tr key={team.id} style={{ borderBottom: '1px solid #dee2e6' }}>
                  <td style={{ padding: '8px' }}>{team.seed || '-'}</td>
                  <td style={{ padding: '8px' }}>{team.name}</td>
                  {tournament.format === 'group_knockout' && <td style={{ padding: '8px' }}>{team.groupName || '-'}</td>}
                  {isAdmin && (
                    <td style={{ padding: '8px', textAlign: 'right' }}>
                      {tournament.status !== 'completed' && (
                        <button className="btn btn-sm btn-outline-danger" onClick={() => handleRemoveTeam(team.id)} disabled={actionLoading}>
                          Remove
                        </button>
                      )}
                    </td>
                  )}
                </tr>
              ))}
              {tournament.teams.length === 0 && (
                <tr><td colSpan={4} style={{ padding: '20px', textAlign: 'center', color: '#6c757d' }}>No teams added yet.</td></tr>
              )}
            </tbody>
          </table>
        </div>
      )}

      {/* Draw Tab */}
      {activeTab === 'draw' && (
        <div>
          {tournament.matches.length === 0 ? (
            <div style={{ textAlign: 'center', padding: '40px' }}>
              <p style={{ color: '#6c757d', marginBottom: '16px' }}>No bracket has been generated yet.</p>
              {isAdmin && tournament.teams.length >= 2 && (
                <button className="btn btn-primary" onClick={handleGenerateDraw} disabled={actionLoading}>
                  {actionLoading ? 'Generating...' : 'Generate Bracket'}
                </button>
              )}
              {tournament.teams.length < 2 && (
                <p style={{ color: '#dc3545' }}>Add at least 2 teams before generating the draw.</p>
              )}
            </div>
          ) : (
            <div>
              <p style={{ color: '#198754', marginBottom: '16px' }}>
                Bracket has been generated with {tournament.matches.length} matches across {tournament.rounds.length} rounds.
              </p>
              {isAdmin && (
                <button className="btn btn-outline-warning" onClick={handleGenerateDraw} disabled={actionLoading}>
                  Regenerate Bracket
                </button>
              )}
              <Link to={`/tournaments/${id}/bracket`} className="btn btn-outline-primary" style={{ marginLeft: '8px' }}>
                View Full Bracket
              </Link>
            </div>
          )}
        </div>
      )}

      {/* Matches Tab */}
      {activeTab === 'matches' && (
        <div>
          {tournament.rounds.length === 0 ? (
            <p style={{ color: '#6c757d' }}>Generate the draw first to see matches.</p>
          ) : (
            tournament.rounds.map(round => (
              <div key={round.id} style={{ marginBottom: '24px' }}>
                <h3 style={{ borderBottom: '1px solid #dee2e6', paddingBottom: '8px' }}>{round.roundName}</h3>
                <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                  <thead>
                    <tr style={{ borderBottom: '1px solid #dee2e6' }}>
                      <th style={{ padding: '6px 8px', textAlign: 'left' }}>#</th>
                      <th style={{ padding: '6px 8px', textAlign: 'left' }}>Home</th>
                      <th style={{ padding: '6px 8px', textAlign: 'center' }}>Score</th>
                      <th style={{ padding: '6px 8px', textAlign: 'left' }}>Away</th>
                      <th style={{ padding: '6px 8px', textAlign: 'left' }}>Status</th>
                      {isAdmin && <th style={{ padding: '6px 8px', textAlign: 'right' }}>Actions</th>}
                    </tr>
                  </thead>
                  <tbody>
                    {roundMatches(round.id).map(match => (
                      <tr key={match.id} style={{ borderBottom: '1px solid #f0f0f0' }}>
                        <td style={{ padding: '6px 8px' }}>{match.bracketPosition}</td>
                        <td style={{ padding: '6px 8px' }}>{match.homeTeam?.name || 'TBD'}</td>
                        <td style={{ padding: '6px 8px', textAlign: 'center' }}>
                          {match.status === 'completed'
                            ? `${match.homeScore} - ${match.awayScore}`
                            : '-'}
                        </td>
                        <td style={{ padding: '6px 8px' }}>{match.awayTeam?.name || 'TBD'}</td>
                        <td style={{ padding: '6px 8px' }}>
                          <span style={{
                            padding: '2px 6px', borderRadius: '4px', fontSize: '12px',
                            backgroundColor: match.status === 'completed' ? '#d1e7dd' : match.status === 'in_progress' ? '#fff3cd' : '#f8f9fa',
                          }}>
                            {match.status}
                          </span>
                        </td>
                        {isAdmin && (
                          <td style={{ padding: '6px 8px', textAlign: 'right' }}>
                            {match.status === 'completed' && tournament.format !== 'round_robin' && (
                              <button className="btn btn-sm btn-outline-success" onClick={() => handleAdvance(match.id)} disabled={actionLoading}>
                                Advance
                              </button>
                            )}
                          </td>
                        )}
                      </tr>
                    ))}
                    {roundMatches(round.id).length === 0 && (
                      <tr><td colSpan={6} style={{ padding: '12px', textAlign: 'center', color: '#6c757d' }}>No matches in this round.</td></tr>
                    )}
                  </tbody>
                </table>
              </div>
            ))
          )}
        </div>
      )}
    </div>
  );
};

export default TournamentManagement;
